/**
 * Patient360SmartSectionGrid.tsx — Renders the 2-column main grid
 * with left/right/full-width zones.
 */

import { memo } from 'react';
import { cn } from '@/lib/utils';
import type {
  Patient360ResolvedSection,
  Patient360Layout,
} from '../../types/dynamicEntityView';
import { Patient360DynamicZone } from './Patient360DynamicZone';

interface Patient360SmartSectionGridProps {
  sections: Patient360ResolvedSection[];
  layout: Patient360Layout;
  programId?: string;
  patientId?: string;
  className?: string;
}

export const Patient360SmartSectionGrid = memo(function Patient360SmartSectionGrid({
  sections,
  layout,
  programId,
  patientId,
  className,
}: Patient360SmartSectionGridProps) {
  const { mainGrid } = layout;
  const leftZones = mainGrid.leftZones ?? ['main_left'];
  const rightZones = mainGrid.rightZones ?? ['main_right'];
  const fullWidthZones = mainGrid.fullWidthZones ?? ['full_width'];

  const hasLeft = leftZones.some((z: string) => sections.some(s => s.zone === z && (!s.isEmpty || s.badges.length > 0)));
  const hasRight = rightZones.some((z: string) => sections.some(s => s.zone === z && (!s.isEmpty || s.badges.length > 0)));
  const hasFullWidth = fullWidthZones.some((z: string) => sections.some(s => s.zone === z && (!s.isEmpty || s.badges.length > 0)));

  return (
    <div className={cn('space-y-3', className)}>
      {/* 2-column grid: left + right */}
      {(hasLeft || hasRight) && (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
          {/* Left column (8/12) */}
          <div className="col-span-12 xl:col-span-8 space-y-3">
            {leftZones.map((zone: string) => (
              <Patient360DynamicZone
                key={zone}
                zoneKey={zone}
                sections={sections}
                programId={programId}
                patientId={patientId}
                columns={3}
              />
            ))}
          </div>

          {/* Right column (4/12) */}
          <div className="col-span-12 xl:col-span-4 space-y-3">
            {rightZones.map((zone: string) => (
              <Patient360DynamicZone
                key={zone}
                zoneKey={zone}
                sections={sections}
                programId={programId}
                patientId={patientId}
                columns={1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Full-width zone */}
      {hasFullWidth && fullWidthZones.map((zone: string) => (
        <Patient360DynamicZone
          key={zone}
          zoneKey={zone}
          sections={sections}
          programId={programId}
          patientId={patientId}
          columns={3}
        />
      ))}
    </div>
  );
});
